// ============================================================================
// STORE.INVITES.JS — Invite-First Provisioning (client CRUD)
//
// Client-side Firestore operations for email invites.
// create/list/revoke run under Firestore security rules (tenant_admin enforced).
// CLAIM is Cloud Function only — see functions.js claimInviteCF.
//
// Invite doc shape (/invites/{inviteId}):
//   inviteId              string   — doc ID mirror
//   tenantId              string
//   tenantNameSnapshot    string|null
//   recipientEmailLower   string   — lowercase for case-insensitive comparison
//   recipientEmailRaw     string   — original casing for display
//   role                  string   — ROLES enum value (≠ platform_admin)
//   specialtyIds          string[] — non-empty iff role == 'specialist'
//   status                'pending' | 'claimed' | 'revoked' | 'expired'
//   createdAt             Timestamp (serverTimestamp)
//   createdByUid          string
//   expiresAt             Timestamp (14 days from creation)
//   claimedAt             Timestamp|null
//   claimedByUid          string|null
//   revokedAt             Timestamp|null
//   revokedByUid          string|null
// ============================================================================

import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { getFirebaseApp } from './store.firebase.js';
import { PATHS } from './firestorePaths.js';

// ---- internal helper --------------------------------------------------------
function getDb() {
  return getFirestore(getFirebaseApp());
}

const INVITE_EXPIRY_DAYS = 14;

// ============================================================================
// createInvite
// Called by tenant admin to create a new invite for a recipient email.
// Returns the new inviteId string.
// ============================================================================
export async function createInvite(
  tenantId,
  createdByUid,
  recipientEmailRaw,
  role,
  specialtyIds = [],
) {
  const db = getDb();
  // Auto-generate ID from the root invites collection
  const ref = doc(collection(db, PATHS.col.invites()));
  const inviteId = ref.id;
  const expiresAt = Timestamp.fromDate(
    new Date(Date.now() + INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
  );

  await setDoc(ref, {
    inviteId,
    tenantId,
    tenantNameSnapshot: null,
    recipientEmailLower: recipientEmailRaw.trim().toLowerCase(),
    recipientEmailRaw: recipientEmailRaw.trim(),
    role,
    specialtyIds: (role === 'specialist' || role === 'specialist_admin') ? (specialtyIds ?? []) : [],
    status: 'pending',
    createdAt: serverTimestamp(),
    createdByUid,
    expiresAt,
    claimedAt: null,
    claimedByUid: null,
    revokedAt: null,
    revokedByUid: null,
  });

  return inviteId;
}

// ============================================================================
// listInvites
// Returns invites for a tenant, optionally filtered by status.
// Default: 'pending'.  Pass null to get all.
// ============================================================================
export async function listInvites(tenantId, statusFilter = 'pending') {
  const db = getDb();
  const constraints = [where('tenantId', '==', tenantId)];
  if (statusFilter != null) {
    constraints.push(where('status', '==', statusFilter));
  }
  const q = query(collection(db, PATHS.col.invites()), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================================================
// revokeInvite
// Tenant admin marks an invite as 'revoked'.
// All other fields must remain unchanged (enforced by Firestore rules).
// ============================================================================
export async function revokeInvite(inviteId, revokedByUid) {
  const db = getDb();
  await updateDoc(doc(db, PATHS.invite(inviteId)), {
    status: 'revoked',
    revokedAt: serverTimestamp(),
    revokedByUid,
  });
}

// ============================================================================
// getPendingInvitesForEmail
// Called by AccessPendingView on mount to discover invites for the signed-in
// user's email address.  Returns array of pending invite docs (may be empty).
// ============================================================================
export async function getPendingInvitesForEmail(emailLower) {
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.invites()),
    where('recipientEmailLower', '==', emailLower),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================================================
// getInvite
// Reads a single invite doc by ID. Returns null if not found.
// Used by AcceptInviteView to display invite details before account creation.
// ============================================================================
export async function getInvite(inviteId) {
  const db = getDb();
  const snap = await getDoc(doc(db, PATHS.invite(inviteId)));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}
